import { Dialog } from "@headlessui/react";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { CommonDataTable } from "../../../common-components/common-material-react-table/CommonMaterialReactTable";
import { IndyBoUnitsHeaders } from "../../../constants/tableConstants/tableHeaders";
import Loading from "../../../common-components/Loader";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  BoUnitRecord,
  clearBoUnitsDetails,
  fetchBoUnitsDetails,
} from "../../../redux/reducers/boUnitsDetailsReducer";
import SelectInput from "../../../common-components/CustomSelect";
import { formatCurrencyOne } from "../../../common-components/commonFunctions";
import { OptionType } from "../../TMM/commonInterfaceTypes";
import { Control, UseFormWatch } from "react-hook-form";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";

interface IndyBoUnitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isbn: string;
  selectedIsbnLabel?: string | null;
  selectedTitleLabel?: string | null;
  imageSrc?: string;
  control: Control<any>;
  watch: UseFormWatch<any>;
  isbnNumberOptions: OptionType[];
  isbnTitleOptions: OptionType[];
  changeIsbnInfo: (event: any) => void;
  isFirefox: boolean;
  hasMoreIsbn: boolean;
}

const IndyBoUnitsModal: React.FC<IndyBoUnitsModalProps> = ({
  isOpen,
  onClose,
  isbn,
  selectedIsbnLabel,
  selectedTitleLabel,
  imageSrc,
  control,
  watch,
  isbnNumberOptions,
  isbnTitleOptions,
  changeIsbnInfo,
  isFirefox,
  hasMoreIsbn,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, isLoading } = useSelector(
    (state: RootState) => state.boUnitsDetailsReducer
  );
  const bgElement = useSelector(
    (state: RootState) => state.dropdownReducer.bgElement
  );

  const [sortBy, setSortBy] = useState<{ heading: string; sortType: 'asc' | 'desc' }>({
    heading: 'ORG_NAME',
    sortType: 'asc'
  });

  useEffect(() => {
    if (isOpen && isbn) {
      dispatch(fetchBoUnitsDetails({ 
        SelectedEbookIsbn: isbn,
        sortBy: sortBy
      }));
    }
    return () => {
      dispatch(clearBoUnitsDetails());
    };
  }, [dispatch, isOpen, isbn, sortBy]);

  const normalizedData = useMemo<BoUnitRecord[]>(() => data ?? [], [data]);

  const totalBoUnits = useMemo(() => {
    return normalizedData.reduce((sum, record) => {
      const value = Number(record.Total_BO_Units ?? 0);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  }, [normalizedData]);

  const tableDataWithTotalRow = useMemo<BoUnitRecord[]>(() => {
    if (!normalizedData.length) return normalizedData;

    const totalRow: BoUnitRecord = {
      ISBN: "",
      Total_BO_Units: totalBoUnits,
      DISTRIBUTION_CHANNEL: null,
      DISTRIBUTION_CHANNEL_NAME: null,
      ORGANIZATION_ID: null,
      ORG_NAME: "Total",
      ACCOUNT_NBR: null,
      ACCOUNT_NAME: "",
      Organization_Label: null,
      Account_Label: null,
      REASON: "",
      isTotalRow: true,
    };

    return [...normalizedData, totalRow];
  }, [normalizedData, totalBoUnits]);

  const formatUnits = (value: number | string | null | undefined): string => {
    const numericValue = Number(value ?? 0);
    if (isNaN(numericValue)) return "-";
    return formatCurrencyOne(numericValue, 0, "");
  };

  const tableStyles = {
    sx: {
      "& thead": {
        position: "sticky",
        top: 0,
        zIndex: bgElement ? 0 : 1,
      },
    },
  };

  const recordCount = normalizedData.length;

  const handleClose = () => {
    onClose();
  };

  const handleExport = () => {
    if (!tableDataWithTotalRow || tableDataWithTotalRow.length === 0) return;

    const rowsForExport = tableDataWithTotalRow.map((row) => ({
      Organization: row.ORG_NAME ?? "",
      Account: row.ACCOUNT_NAME ?? "",
      Reason: row.REASON ?? "",
      "Total BO Units": formatUnits(row.Total_BO_Units),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rowsForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Indy BO Units");

    const safeIsbn =
      (isbn && isbn.toString().replace(/[^0-9A-Za-z]/g, "")) ||
      "indy_bo_units";
    const fileName = `${safeIsbn}_indy_bo_units.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Custom header renderer with sort arrows (matching Title List pattern)
  const customHeaderRenderer = (header: string, accessorKey: string) => {
    const sortableColumns = ['ORG_NAME', 'ACCOUNT_NAME', 'REASON', 'Total_BO_Units'];
    const isSortable = sortableColumns.includes(accessorKey);
    const isActive = sortBy.heading === accessorKey;

    if (!isSortable) {
      return header;
    }

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <span>{header}</span>
        <span>
          {isActive && sortBy.sortType === "desc" ? (
            <FaSortUp
              className="cursor-pointer"
              title={`sort ${header} by ascending`}
              onClick={() => {
                setSortBy({
                  sortType: "asc",
                  heading: accessorKey,
                });
              }}
            />
          ) : isActive && sortBy.sortType === "asc" ? (
            <FaSortDown
              title={`sort ${header} by descending`}
              className="cursor-pointer"
              onClick={() => {
                setSortBy({
                  sortType: "desc",
                  heading: accessorKey,
                });
              }}
            />
          ) : (
            <FaSort
              title={`sort ${header} by ascending`}
              className="cursor-pointer"
              onClick={() => {
                setSortBy({
                  sortType: "asc",
                  heading: accessorKey,
                });
              }}
            />
          )}
        </span>
      </div>
    );
  };

  return (
    // Use a no-op onClose so Headless UI does NOT auto-close the dialog
    // when clicking inside react-select's body portal. We handle closing
    // explicitly via the overlay div and the close button.
    <Dialog open={isOpen} onClose={() => {}}>
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div
          className="absolute inset-0 bg-black opacity-40"
          onClick={handleClose}
        ></div>
        <div className="relative bg-white rounded-lg shadow-2xl w-[95vw] max-w-7xl mx-auto max-h-[95vh] flex flex-col">
          {/* Close button in top-right corner of popup */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-600"
            title="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>

          {/* Header bar copied to match TitleDetails design */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="py-2 flex gap-4 items-center">
              <div className="flex items-center">
                <div className="w-[56px] h-[80px] border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-white">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt="Book cover"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 text-center px-2">
                      No Cover
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-4">
                <div className="flex gap-4">
                  <div className="w-[30%]">
                    <p className="text-[#e31c23] font-bold">ISBN</p>
                    <div className="w-full grid grid-cols-1">
                      <SelectInput
                        control={control}
                        name="isbn"
                        options={isbnNumberOptions}
                        placeholder="Select ISBN"
                        onChangeCallback={changeIsbnInfo}
                        watch={watch}
                        isFirefox={isFirefox}
                        hasMoreIsbn={hasMoreIsbn}
                        copyBtn={true}
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <p className="text-[#e31c23] font-bold">BOOK TITLE</p>
                    <div className="w-full grid grid-cols-1">
                      <SelectInput
                        control={control}
                        name="title"
                        options={isbnTitleOptions}
                        placeholder="Select Book Title"
                        onChangeCallback={changeIsbnInfo}
                        watch={watch}
                        isFirefox={isFirefox}
                        hasMoreIsbn={hasMoreIsbn}
                        copyBtn={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Title bar */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-2 text-white font-semibold text-lg flex justify-between items-center">
            <span>Indy Backorder Units</span>
            <div className="flex items-center gap-4">
              <span className="text-sm font-normal">
                Total Records: {recordCount}
              </span>
              {tableDataWithTotalRow.length > 0 && (
                <button
                  type="button"
                  onClick={handleExport}
                  className="px-4 py-1.5 text-sm font-semibold bg-white text-[#e31c23] border border-[#e31c23] rounded shadow hover:bg-gray-100"
                >
                  Export
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <Dialog.Panel className="px-6 py-4 overflow-hidden flex-1 flex flex-col">
            <Dialog.Description className="flex-1 flex flex-col">
              <Loading isLoading={isLoading}>
                {/* Table */}
                <div className="flex-1 overflow-hidden border border-gray-200 rounded-lg mt-2">
                  <CommonDataTable
                    muiTableProps={tableStyles}
                    tableCursor="default"
                    maxHeight="65vh"
                    enableRowStyling={true}
                    tableHeaders={IndyBoUnitsHeaders}
                    columnOrder={["ORG_NAME", "ACCOUNT_NAME", "REASON", "Total_BO_Units"]}
                    className="h-full"
                    data={tableDataWithTotalRow}
                    enableGlobalFilter={false}
                    enableBottomToolbar={false}
                    showRowNumbers={false}
                    scrollable
                    pageSize={tableDataWithTotalRow?.length}
                    callback={() => undefined}
                    isLoading={isLoading}
                    NodataFound={
                      isbn
                        ? "No BO unit records found for the selected ISBN."
                        : "Provide an ISBN to view BO unit records."
                    }
                    enableTopToolbar={false}
                    customHeaderRenderer={customHeaderRenderer}
                    changeSelectedColumnDataDesign={["Total_BO_Units"]}
                    changedDataCellColumn={(header, accessor) => {
                      if (accessor === "Total_BO_Units") {
                        return {
                          accessorKey: accessor,
                          header: header,
                          Cell: ({ row }: { row: any }) => {
                            const original = row.original as any;
                            const value = original?.[accessor];

                            if (original?.isTotalRow) {
                              return (
                                <span className="font-bold">
                                  {formatUnits(value)}
                                </span>
                              );
                            }

                            return <span>{formatUnits(value)}</span>;
                          },
                          Header: () =>
                            customHeaderRenderer(
                              header,
                              accessor
                            ) as React.ReactElement,
                        };
                      }

                      return {
                        accessorKey: accessor,
                        header: header,
                        Cell: ({ row }: { row: any }) => {
                          const original = row.original as any;
                          const value = original?.[accessor];
                          return <span>{value ?? ""}</span>;
                        },
                        Header: () =>
                          customHeaderRenderer(
                            header,
                            accessor
                          ) as React.ReactElement,
                      };
                    }}
                  />
                </div>
              </Loading>
            </Dialog.Description>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default IndyBoUnitsModal;


